import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_URL } from '../../constant';
import './CaptainTCCardAgent.css';
import captainTcImg from '../../images/captain-tc/captain-tc-card.jpeg';
import wallet2 from "../../images/shapes/Wallet03.1 1.png";
import oldbank from "../../images/shapes/bank-pound-icon2 4.png";

function CaptainTCCardAgent() {
    const outletContext = useOutletContext() || {};
    const { userData } = outletContext;

    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([
        { tier: '£5', price: '£ 5.00', owned: false, serial: null },
        { tier: '£10', price: '£ 10.00', owned: false, serial: null },
        { tier: '£25', price: '£ 25.00', owned: false, serial: null },
    ]);

    // Sales history
    const [salesHistory, setSalesHistory] = useState([]);
    const [salesLoading, setSalesLoading] = useState(false);

    // Agent TopCoin Wallet
    const [agentWalletBalance, setAgentWalletBalance] = useState(0);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('auth_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }, []);

    const fetchCardStatus = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/agent/captain-tc-cards`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.cards) {
                    setCards(prev => prev.map(card => {
                        const apiCard = data.cards.find(c =>
                            c.tier === card.tier || c.price === card.price
                        );
                        if (apiCard) {
                            return {
                                ...card,
                                owned: apiCard.owned || false,
                                serial: apiCard.serial || null,
                            };
                        }
                        return card;
                    }));
                }
            }
        } catch (err) {
            console.warn('Captain TC Cards Agent API not available:', err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    const fetchSalesHistory = useCallback(async () => {
        try {
            setSalesLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/agent/captain-tc-cards/sales-history`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setSalesHistory(data.sales || []);
                    setAgentWalletBalance(data.agent_wallet_balance || 0);
                }
            }
        } catch (err) {
            console.warn('Sales history API not available:', err.message);
        } finally {
            setSalesLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchCardStatus();
        fetchSalesHistory();
    }, [fetchCardStatus, fetchSalesHistory]);

    const tierColors = {
        '£5': { accent: '#cd7f32', gradient: 'linear-gradient(135deg, #cd7f32, #a0622e)', label: 'Bronze' },
        '£10': { accent: '#C0C0C0', gradient: 'linear-gradient(135deg, #C0C0C0, #a8a8a8)', label: 'Silver' },
        '£25': { accent: '#FFD700', gradient: 'linear-gradient(135deg, #FFD700, #daa520)', label: 'Gold' },
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return dateString;
        }
    };

    if (loading) {
        return (
            <div className="captain-tc-agent-wrapper">
                <div className="captain-tc-agent-loading">
                    <div className="captain-tc-agent-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="captain-tc-agent-wrapper">
            {/* Header */}
            <div className="captain-tc-agent-header">
                <div className="captain-tc-agent-header-left">
                    <span className="captain-tc-agent-label">Captain TC</span>
                    <h2 className="captain-tc-agent-title">Cash-Wallet Card</h2>
                </div>
                <div className="captain-tc-agent-header-right">
                    <img
                        src={captainTcImg}
                        alt="Captain TC Card"
                        className="captain-tc-agent-thumb"
                    />
                </div>
            </div>

            {/* Card Tiers - Owned Status */}
            <div className="captain-tc-agent-cards-grid">
                {cards.map((card) => {
                    const colors = tierColors[card.tier];
                    return (
                        <div
                            key={card.tier}
                            className={`captain-tc-agent-card-item ${card.owned ? 'owned' : 'not-owned'}`}
                        >
                            <div
                                className="captain-tc-agent-card-accent"
                                style={{ background: colors.gradient }}
                            ></div>

                            <div className="captain-tc-agent-card-body">
                                <div className="captain-tc-agent-card-price-row">
                                    <span
                                        className="captain-tc-agent-card-price"
                                        style={{ color: colors.accent }}
                                    >
                                        {card.price}
                                    </span>
                                    <span
                                        className={`captain-tc-agent-owned-badge ${card.owned ? 'badge-yes' : 'badge-no'}`}
                                    >
                                        {card.owned ? '✓ Owned' : 'Not Owned'}
                                    </span>
                                </div>

                                <div className="captain-tc-agent-card-tier-label">
                                    {colors.label} Card
                                </div>

                                {card.owned && card.serial && (
                                    <div className="captain-tc-agent-serial">
                                        Serial: <strong>{card.serial}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Sales History Section */}
            <div className="captain-tc-agent-sales-section">
                <h3 className="captain-tc-agent-sales-title">
                    The TopCoin Reward Promotion
                    <span className="captain-tc-agent-sales-subtitle">
                        (As the Agent Sells the Cards to the Customer)
                    </span>
                </h3>

                <div className="captain-tc-agent-table-container">
                    <table className="captain-tc-agent-table">
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Serial</th>
                                <th>Price</th>
                                <th>Date</th>
                                <th>TopCoin Earned</th>
                                <th>History</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesHistory.length > 0 ? (
                                salesHistory.map((sale, index) => (
                                    <tr key={sale.id || index}>
                                        <td>{index + 1}</td>
                                        <td className="serial-cell">{sale.serial || '—'}</td>
                                        <td>{sale.price || '—'}</td>
                                        <td>{formatDate(sale.date)}</td>
                                        <td className="topcoin-cell">
                                            {sale.topcoin_earned
                                                ? sale.topcoin_earned.toLocaleString()
                                                : '—'}
                                        </td>
                                        <td>
                                            <button
                                                className="captain-tc-agent-history-btn"
                                                disabled={!sale.serial}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <>
                                    {[1, 2, 3, 4].map((n) => (
                                        <tr key={n} className="empty-row">
                                            <td>{n}</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                            <td>—</td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Agent TopCoin Wallet */}
            <div className="captain-tc-agent-wallet-section">
                <div className="captain-tc-agent-wallet-row">
                    <div className="captain-tc-agent-wallet-info">
                        <div className="captain-tc-agent-wallet-icon">
                            <img src={wallet2} alt="Wallet" style={{ backgroundColor: "white" }} />
                        </div>
                        <div>
                            <h4 className="captain-tc-agent-wallet-title">Advertising Agent TopCoin Wallet</h4>
                            <span className="captain-tc-agent-wallet-balance">
                                {agentWalletBalance.toLocaleString()} TC
                            </span>
                        </div>
                    </div>
                </div>

                <div className="captain-tc-agent-withdraw-row">
                    <div className="captain-tc-agent-withdraw-info">
                        <div className="captain-tc-agent-withdraw-icon">
                            <img src={oldbank} alt="Bank" style={{ backgroundColor: "white" }} />
                        </div>
                        <h4 className="captain-tc-agent-withdraw-title">Withdraw</h4>
                    </div>
                    <button className="captain-tc-agent-withdraw-btn" disabled>
                        Withdraw
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CaptainTCCardAgent;
